/**
 * Public Help & Support Page
 * 
 * End user FAQ viewing experience with refined UI/UX.
 * 
 * Design Principles:
 * - Optimal reading width (max 720px for content)
 * - Generous whitespace for visual breathing room
 * - Clear visual hierarchy
 * - Subtle animations and transitions
 * 
 * Features:
 * - Centered, constrained layout for large screens
 * - Sticky search bar
 * - Category tabs (desktop) / dropdown (mobile)
 * - Accordion-style FAQ list (one expanded at a time)
 * - Lazy-load media on expand
 * - Keyword highlighting in search results
 * - Full keyboard navigation (WCAG 2.1 compliant)
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  Drawer,
  Typography,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Skeleton,
  Alert,
  Stack,
  Container,
  useTheme,
  useMediaQuery,
  Fade,
  IconButton,
  Tooltip,
  InputLabel,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import YouTubeIcon from '@mui/icons-material/YouTube';
import LoomIcon from '@mui/icons-material/VideoLibrary';
import DocsIcon from '@mui/icons-material/Description';
import SheetsIcon from '@mui/icons-material/TableChart';
import SlidesIcon from '@mui/icons-material/Slideshow';
import DriveIcon from '@mui/icons-material/Folder';
import EmptyIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import ScrollTopIcon from '@mui/icons-material/KeyboardArrowUp';
import AddIcon from '@mui/icons-material/Add';
import TicketIcon from '@mui/icons-material/ConfirmationNumber';
import BackIcon from '@mui/icons-material/ArrowBack';
import { debounce } from 'lodash';
import { useSelector } from 'react-redux';

import MainCard from '../../../components/MainCard';
import {
  useGetPublicFaqsQuery,
  useGetPublicFaqCategoriesQuery,
  useGetMySupportTicketsQuery,
  useGetMySupportTicketByIdQuery,
} from '../../../store/slices/apiSlice';
import SupportTicketForm from '../../../components/common/SupportTicketForm';
import { useResponsive } from '../../../hooks/ResponsiveProvider';
import { getStatusConfig } from '../../../util/supportTicketConstants';

// Layout constants for optimal reading experience
const CONTENT_MAX_WIDTH = 800; // Optimal reading width
const VIDEO_MAX_WIDTH = 560; // 16:9 aspect at comfortable size

// Get embed URL for media
const getEmbedUrl = (provider, resourceId) => {
  if (!resourceId) return null;
  switch (provider) {
    case 'YOUTUBE':
      return `https://www.youtube.com/embed/${resourceId}`;
    case 'LOOM':
      return `https://www.loom.com/embed/${resourceId}`;
    case 'GOOGLE_DRIVE':
      return `https://drive.google.com/file/d/${resourceId}/preview`;
    case 'GOOGLE_DOCS':
      return `https://docs.google.com/document/d/${resourceId}/preview`;
    case 'GOOGLE_SHEETS':
      return `https://docs.google.com/spreadsheets/d/${resourceId}/preview`;
    case 'GOOGLE_SLIDES':
      return `https://docs.google.com/presentation/d/${resourceId}/embed`;
    default:
      return null;
  }
};

// Check if provider is video type
const isVideoProvider = (provider) => ['YOUTUBE', 'LOOM'].includes(provider);

// Provider icon component
const ProviderIcon = ({ provider, size = 18 }) => {
  const iconProps = { sx: { fontSize: size } };
  switch (provider) {
    case 'YOUTUBE':
      return <YouTubeIcon {...iconProps} sx={{ ...iconProps.sx, color: '#FF0000' }} />;
    case 'LOOM':
      return <LoomIcon {...iconProps} sx={{ ...iconProps.sx, color: '#625DF5' }} />;
    case 'GOOGLE_DOCS':
      return <DocsIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4285F4' }} />;
    case 'GOOGLE_SHEETS':
      return <SheetsIcon {...iconProps} sx={{ ...iconProps.sx, color: '#0F9D58' }} />;
    case 'GOOGLE_SLIDES':
      return <SlidesIcon {...iconProps} sx={{ ...iconProps.sx, color: '#F4B400' }} />;
    case 'GOOGLE_DRIVE':
      return <DriveIcon {...iconProps} sx={{ ...iconProps.sx, color: '#4285F4' }} />;
    default:
      return null;
  }
};

// Get provider display name
const getProviderName = (provider) => {
  const names = {
    YOUTUBE: 'Video Tutorial',
    LOOM: 'Video Tutorial',
    GOOGLE_DOCS: 'Document',
    GOOGLE_SHEETS: 'Spreadsheet',
    GOOGLE_SLIDES: 'Presentation',
    GOOGLE_DRIVE: 'File',
  };
  return names[provider] || 'Media';
};

// Highlight search terms in text
const highlightText = (text, searchTerm) => {
  if (!searchTerm || !text) return text;
  
  const parts = text.split(new RegExp(`(${searchTerm})`, 'gi'));
  return parts.map((part, index) =>
    part.toLowerCase() === searchTerm.toLowerCase() ? (
      <mark key={index} style={{ backgroundColor: '#FFF59D', padding: '0 2px', borderRadius: 2 }}>
        {part}
      </mark>
    ) : (
      part
    )
  );
};

// Media embed component (lazy loaded) with constrained width
const MediaEmbed = ({ provider, resourceId, isExpanded }) => {
  const [shouldLoad, setShouldLoad] = useState(false);
  const embedUrl = getEmbedUrl(provider, resourceId);

  // Only load media after accordion has been expanded
  useEffect(() => {
    if (isExpanded && !shouldLoad) {
      // Small delay to let accordion animation complete
      const timer = setTimeout(() => setShouldLoad(true), 300);
      return () => clearTimeout(timer);
    }
  }, [isExpanded, shouldLoad]);

  if (!embedUrl) return null;

  const isVideo = isVideoProvider(provider);

  return (
    <Box 
      sx={{ 
        mt: 3,
        pt: 3,
        borderTop: 1,
        borderColor: 'divider',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <ProviderIcon provider={provider} />
        <Typography variant="caption" fontWeight={500} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
          {getProviderName(provider)}
        </Typography>
      </Stack>
      {/* Constrained media container for large screens */}
      <Box
        sx={{
          maxWidth: VIDEO_MAX_WIDTH,
          width: '100%',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            paddingBottom: isVideo ? '56.25%' : '75%', // 16:9 for video, 4:3 for docs
            height: 0,
            overflow: 'hidden',
            borderRadius: 1.5,
            backgroundColor: isVideo ? 'grey.900' : 'grey.100',
          }}
        >
          {shouldLoad ? (
            <iframe
              src={embedUrl}
              title="FAQ Media"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              loading="lazy"
            />
          ) : (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography variant="body2" color={isVideo ? 'grey.400' : 'text.secondary'}>
                Loading {isVideo ? 'video' : 'document'}...
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

// Empty state component
const EmptyState = ({ hasSearch, searchTerm }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: { xs: 6, md: 10 },
      px: 3,
      textAlign: 'center',
    }}
  >
    <Box
      sx={{
        width: 80,
        height: 80,
        borderRadius: '50%',
        backgroundColor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mb: 3,
      }}
    >
      <EmptyIcon sx={{ fontSize: 40, color: 'grey.400' }} />
    </Box>
    <Typography variant="h6" fontWeight={600} color="text.primary" gutterBottom>
      {hasSearch ? 'No results found' : 'No FAQs available'}
    </Typography>
    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 360, lineHeight: 1.6 }}>
      {hasSearch
        ? `We couldn't find any FAQs matching "${searchTerm}". Try different keywords or browse by category.`
        : 'Check back later for helpful articles and guides.'}
    </Typography>
  </Box>
);

// Loading skeleton for FAQs
const FaqSkeleton = () => (
  <Stack spacing={1}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Paper key={i} variant="outlined" sx={{ p: 2 }}>
        <Skeleton variant="text" width="70%" height={28} />
      </Paper>
    ))}
  </Stack>
);

// Single FAQ Accordion Item with refined styling
const FaqAccordionItem = ({ faq, expanded, onChange, searchTerm }) => {
  const hasMedia = faq.videoProvider !== 'NONE' && faq.videoId;

  return (
    <Accordion
      expanded={expanded}
      onChange={onChange}
      disableGutters
      elevation={0}
      sx={{
        '&:before': { display: 'none' },
        backgroundColor: 'transparent',
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-of-type': {
          borderBottom: 0,
        },
        '&.Mui-expanded': {
          backgroundColor: 'grey.50',
          borderRadius: 2,
          borderBottom: 0,
          mb: 1,
        },
        transition: 'background-color 0.2s ease',
      }}
      TransitionProps={{ unmountOnExit: true }}
    >
      <AccordionSummary
        expandIcon={
          <ExpandIcon 
            sx={{ 
              fontSize: 20,
              color: 'text.secondary',
              transition: 'transform 0.2s ease',
            }} 
          />
        }
        aria-controls={`faq-${faq.id}-content`}
        id={`faq-${faq.id}-header`}
        sx={{
          minHeight: 64,
          px: { xs: 0, md: 2 },
          '&.Mui-expanded': {
            minHeight: 64,
          },
          '& .MuiAccordionSummary-content': {
            my: 2,
          },
          '&:hover': {
            backgroundColor: expanded ? 'transparent' : 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pr: 1 }}>
          <Typography 
            variant="body1" 
            fontWeight={500}
            sx={{ 
              flex: 1,
              color: expanded ? 'primary.main' : 'text.primary',
              transition: 'color 0.2s ease',
              fontSize: { xs: '0.95rem', md: '1rem' },
              lineHeight: 1.5,
            }}
          >
            {highlightText(faq.question, searchTerm)}
          </Typography>
          {hasMedia && (
            <Tooltip title={`Includes ${getProviderName(faq.videoProvider).toLowerCase()}`} arrow placement="top">
              <Box sx={{ ml: 1.5, display: 'flex', alignItems: 'center' }}>
                <ProviderIcon provider={faq.videoProvider} size={16} />
              </Box>
            </Tooltip>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails 
        sx={{ 
          pt: 0, 
          pb: 3, 
          px: { xs: 0, md: 2 },
        }}
      >
        {/* Answer Content with constrained width and tight spacing */}
        <Box 
          sx={{ 
            maxWidth: 680,
            // Reset all margins and paddings from rich text editor output
            '& > *:first-of-type': { mt: 0 },
            '& > *:last-child': { mb: 0 },
          }}
        >
          <Typography 
            variant="body2" 
            component="div"
            sx={{ 
              color: 'text.secondary',
              lineHeight: 1.6,
              fontSize: '0.9rem',
              // Paragraph styling - minimal spacing
              '& p': { 
                m: 0,
                mb: 0.5,
                '&:last-child': { mb: 0 },
                '&:empty': { display: 'none' }, // Hide empty paragraphs
              },
              // Remove extra spacing from br tags
              '& br': {
                lineHeight: 0.5,
              },
              // Lists - compact styling
              '& ul, & ol': { 
                pl: 2, 
                my: 0.5,
                '& li': { 
                  mb: 0.25,
                  lineHeight: 1.5,
                  '& p': { m: 0 }, // Remove p margins inside li
                },
              },
              // Bold/Strong text
              '& strong, & b': {
                color: 'text.primary',
                fontWeight: 600,
              },
              // Italic
              '& em, & i': {
                fontStyle: 'italic',
              },
              // Underline
              '& u': {
                textDecoration: 'underline',
              },
              // Code
              '& code': {
                backgroundColor: 'grey.100',
                padding: '1px 4px',
                borderRadius: 0.5,
                fontFamily: 'monospace',
                fontSize: '0.85em',
              },
              // Blockquote - compact
              '& blockquote': {
                borderLeft: 2,
                borderColor: 'grey.300',
                pl: 1.5,
                ml: 0,
                my: 0.5,
                color: 'text.secondary',
                '& p': { m: 0 },
              },
              // Headers inside content
              '& h1, & h2, & h3': {
                color: 'text.primary',
                mt: 1,
                mb: 0.5,
                lineHeight: 1.3,
                '&:first-of-type': { mt: 0 },
              },
              '& h1': { fontSize: '1.25rem', fontWeight: 600 },
              '& h2': { fontSize: '1.1rem', fontWeight: 600 },
              '& h3': { fontSize: '1rem', fontWeight: 600 },
            }}
            dangerouslySetInnerHTML={{ 
              __html: searchTerm 
                ? faq.answer.replace(
                    new RegExp(`(${searchTerm})`, 'gi'),
                    '<mark style="background-color: #FFF59D; padding: 0 2px; border-radius: 2px;">$1</mark>'
                  )
                : faq.answer 
            }}
          />
        </Box>

        {/* Media (lazy loaded) */}
        {hasMedia && (
          <MediaEmbed
            provider={faq.videoProvider}
            resourceId={faq.videoId}
            isExpanded={expanded}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const HelpSupport = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const { isSmallScreen } = useResponsive();
  const userData = useSelector((state) => state.auth.userData);
  
  // Refs
  const searchRef = useRef(null);
  const contentRef = useRef(null);
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [createTicketDrawerOpen, setCreateTicketDrawerOpen] = useState(false);
  const [myTicketsDrawerOpen, setMyTicketsDrawerOpen] = useState(false);
  const [selectedMyTicketId, setSelectedMyTicketId] = useState(null);
  const [myTicketsPage, setMyTicketsPage] = useState(1);

  // API queries
  const { data: categoriesData, isLoading: categoriesLoading } = useGetPublicFaqCategoriesQuery();
  const { data: faqsData, isLoading: faqsLoading, error: faqsError } = useGetPublicFaqsQuery({
    search: debouncedSearch,
    categoryId: selectedCategory !== 'all' ? selectedCategory : undefined,
    limit: 100,
  });
  const { data: myTicketsData, isLoading: myTicketsLoading } = useGetMySupportTicketsQuery(
    { page: myTicketsPage, limit: 20 },
    { skip: !myTicketsDrawerOpen },
  );
  const { data: myTicketDetail, isLoading: myTicketDetailLoading } = useGetMySupportTicketByIdQuery(selectedMyTicketId, {
    skip: !selectedMyTicketId,
  });

  // Debounced search handler
  const debouncedSetSearch = useMemo(
    () => debounce((value) => {
      setDebouncedSearch(value);
      setExpandedFaq(null);
    }, 300),
    []
  );

  const handleSearchChange = useCallback((e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedSetSearch(value);
  }, [debouncedSetSearch]);

  const handleClearSearch = () => {
    setSearchQuery('');
    setDebouncedSearch('');
    searchRef.current?.focus();
  };

  const handleCategoryChange = (event, newValue) => {
    setSelectedCategory(newValue || event.target.value);
    setExpandedFaq(null);
  };

  const handleAccordionChange = (faqId) => (event, isExpanded) => {
    setExpandedFaq(isExpanded ? faqId : null);
  };

  // Scroll detection
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const categories = categoriesData || [];
  const faqs = faqsData?.data || [];
  const hasSearch = Boolean(debouncedSearch);

  // Group FAQs by category for display
  const faqsByCategory = useMemo(() => {
    if (selectedCategory !== 'all' || hasSearch) {
      return { [selectedCategory]: faqs };
    }
    
    const grouped = {};
    faqs.forEach((faq) => {
      const catId = faq.category?.id || 'uncategorized';
      if (!grouped[catId]) {
        grouped[catId] = [];
      }
      grouped[catId].push(faq);
    });
    return grouped;
  }, [faqs, selectedCategory, hasSearch]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: 'background.default' }}>
      {/* Centered Container for optimal reading width */}
      <Box
        sx={{
          maxWidth: CONTENT_MAX_WIDTH,
          mx: 'auto',
          px: { xs: 2, sm: 3, md: 4 },
          py: { xs: 3, md: 4 },
        }}
      >
        {/* Page Header */}
        <Box sx={{ mb: { xs: 3, md: 4 }, textAlign: { xs: 'left', md: 'center' } }}>
          <Typography 
            variant="h4" 
            fontWeight={700}
            sx={{ 
              fontSize: { xs: '1.5rem', md: '2rem' },
              color: 'text.primary',
              mb: 1,
            }}
          >
            Help & Support
          </Typography>
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', md: '1rem' }, mb: 2 }}
          >
            Find answers to frequently asked questions
          </Typography>
          <Stack direction="row" spacing={1} justifyContent={{ xs: 'stretch', md: 'center' }} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<AddIcon />}
              onClick={() => setCreateTicketDrawerOpen(true)}
              sx={{ textTransform: 'none' }}
            >
              Create ticket
            </Button>
            <Button
              variant="outlined"
              size="medium"
              startIcon={<TicketIcon />}
              onClick={() => { setSelectedMyTicketId(null); setMyTicketsDrawerOpen(true); }}
              sx={{ textTransform: 'none' }}
            >
              My tickets
            </Button>
          </Stack>
        </Box>

        {/* Search Bar */}
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            position: 'sticky',
            top: 16,
            zIndex: 10,
          }}
        >
          <TextField
            fullWidth
            inputRef={searchRef}
            placeholder="Search for answers..."
            value={searchQuery}
            onChange={handleSearchChange}
            variant="outlined"
            size={isMobile ? 'small' : 'medium'}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                borderRadius: 2,
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.2s ease',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
                },
                '&.Mui-focused': {
                  boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            aria-label="Search FAQs"
          />
        </Box>

        {/* Category Navigation */}
        {!categoriesLoading && categories.length > 0 && (
          <Box sx={{ mb: { xs: 3, md: 4 } }}>
            {isMobile ? (
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  label="Category"
                  sx={{
                    backgroundColor: 'background.paper',
                    borderRadius: 1.5,
                  }}
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {categories.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Tabs
                  value={selectedCategory}
                  onChange={handleCategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    '& .MuiTabs-indicator': {
                      height: 2,
                      borderRadius: 1,
                    },
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      minHeight: 44,
                      px: 2,
                      color: 'text.secondary',
                      '&.Mui-selected': {
                        color: 'primary.main',
                        fontWeight: 600,
                      },
                    },
                  }}
                >
                  <Tab value="all" label="All" />
                  {categories.map((cat) => (
                    <Tab key={cat.id} value={cat.id} label={cat.name} />
                  ))}
                </Tabs>
              </Box>
            )}
          </Box>
        )}

        {/* Search Results Count */}
        {hasSearch && !faqsLoading && (
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mb: 2,
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {faqs.length} result{faqs.length !== 1 ? 's' : ''} for "<strong>{debouncedSearch}</strong>"
          </Typography>
        )}

        {/* Error State */}
        {faqsError && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
            }}
          >
            Failed to load FAQs. Please try again later.
          </Alert>
        )}

        {/* Content */}
        <Box ref={contentRef}>
          {faqsLoading ? (
            <FaqSkeleton />
          ) : faqs.length === 0 ? (
            <EmptyState hasSearch={hasSearch} searchTerm={debouncedSearch} />
          ) : selectedCategory === 'all' && !hasSearch ? (
            // Show FAQs grouped by category
            <Stack spacing={{ xs: 4, md: 5 }}>
              {Object.entries(faqsByCategory).map(([categoryId, categoryFaqs]) => {
                const category = categories.find((c) => c.id === categoryId);
                if (!category || categoryFaqs.length === 0) return null;

                return (
                  <Box key={categoryId}>
                    <Typography 
                      variant="overline" 
                      sx={{ 
                        display: 'block',
                        mb: 2,
                        color: 'text.secondary',
                        fontWeight: 600,
                        letterSpacing: 1,
                        fontSize: '0.75rem',
                      }}
                    >
                      {category.name}
                    </Typography>
                    <Paper 
                      variant="outlined" 
                      sx={{ 
                        borderRadius: 2,
                        overflow: 'hidden',
                        borderColor: 'divider',
                      }}
                    >
                      {categoryFaqs.map((faq, index) => (
                        <FaqAccordionItem
                          key={faq.id}
                          faq={faq}
                          expanded={expandedFaq === faq.id}
                          onChange={handleAccordionChange(faq.id)}
                          searchTerm={debouncedSearch}
                        />
                      ))}
                    </Paper>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            // Show flat list (filtered by category or search)
            <Paper 
              variant="outlined" 
              sx={{ 
                borderRadius: 2,
                overflow: 'hidden',
                borderColor: 'divider',
              }}
            >
              {faqs.map((faq) => (
                <FaqAccordionItem
                  key={faq.id}
                  faq={faq}
                  expanded={expandedFaq === faq.id}
                  onChange={handleAccordionChange(faq.id)}
                  searchTerm={debouncedSearch}
                />
              ))}
            </Paper>
          )}
        </Box>

        {/* Footer spacing */}
        <Box sx={{ height: { xs: 60, md: 80 } }} />
      </Box>

      {/* Scroll to Top Button */}
      <Fade in={showScrollTop}>
        <Box
          sx={{
            position: 'fixed',
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 1000,
          }}
        >
          <Tooltip title="Scroll to top" placement="left">
            <IconButton
              onClick={scrollToTop}
              size={isMobile ? 'medium' : 'large'}
              sx={{
                backgroundColor: 'background.paper',
                color: 'text.primary',
                boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                border: 1,
                borderColor: 'divider',
                '&:hover': {
                  backgroundColor: 'grey.100',
                },
              }}
              aria-label="Scroll to top"
            >
              <ScrollTopIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Fade>

      {/* Create ticket drawer – form unmounts when closed to avoid stale state */}
      <Drawer
        anchor="right"
        open={createTicketDrawerOpen}
        onClose={() => setCreateTicketDrawerOpen(false)}
        ModalProps={{
          'aria-labelledby': 'create-ticket-drawer-title',
          keepMounted: false,
        }}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isSmallScreen ? '100%' : 480,
            height: '100%',
            pt: 4,
            pb: 2,
            px: 3,
            backgroundColor: '#F5F5F5',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', pt: 5 }} role="region">
          <Typography id="create-ticket-drawer-title" sx={{ fontSize: 17, fontWeight: 600, color: '#000', mb: 1 }}>
            Create support ticket
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Submit a support request. We will get back to you shortly.
          </Typography>
          {createTicketDrawerOpen && (
            <SupportTicketForm
              initialValues={{
                userName: userData?.firstName || userData?.name || '',
                email: userData?.email || '',
                organizationName: userData?.organizationName || userData?.organization || '',
              }}
              onSuccess={() => setCreateTicketDrawerOpen(false)}
              onClose={() => setCreateTicketDrawerOpen(false)}
            />
          )}
        </Box>
      </Drawer>

      {/* My tickets drawer */}
      <Drawer
        anchor="right"
        open={myTicketsDrawerOpen}
        onClose={() => { setMyTicketsDrawerOpen(false); setSelectedMyTicketId(null); setMyTicketsPage(1); }}
        ModalProps={{
          'aria-labelledby': myTicketsDrawerOpen ? (selectedMyTicketId ? 'my-ticket-detail-title' : 'my-tickets-title') : 'my-tickets-title',
          keepMounted: false,
        }}
        sx={{
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: isSmallScreen ? '100%' : 480,
            height: '100%',
            pt: 4,
            pb: 2,
            px: 3,
            backgroundColor: '#F5F5F5',
            boxSizing: 'border-box',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', pt: 5 }} role="region">
          {selectedMyTicketId ? (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Button startIcon={<BackIcon />} onClick={() => setSelectedMyTicketId(null)} size="small" sx={{ textTransform: 'none', minWidth: 'auto', px: 1 }} aria-label="Back to tickets list">
                    Back
                  </Button>
                  <Typography id="my-ticket-detail-title" sx={{ fontSize: 17, fontWeight: 600, color: '#000' }}>
                    Ticket details
                  </Typography>
                </Stack>
              </Stack>
              {myTicketDetailLoading ? (
                <Skeleton variant="rectangular" height={200} />
              ) : myTicketDetail ? (
                <Box sx={{ overflow: 'auto', flex: 1 }}>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" display="block">Status</Typography>
                    <Chip
                      label={getStatusConfig(myTicketDetail.status).label}
                      size="small"
                      sx={{
                        backgroundColor: getStatusConfig(myTicketDetail.status).color,
                        color: getStatusConfig(myTicketDetail.status).text,
                        mt: 0.5,
                      }}
                    />
                    <Typography variant="body2" sx={{ mt: 2 }}><strong>Description:</strong> {myTicketDetail.description}</Typography>
                    {myTicketDetail.problemType && (
                      <Typography variant="body2" sx={{ mt: 1 }}><strong>Problem type:</strong> {myTicketDetail.problemType}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
                      Created {myTicketDetail.createdAt ? new Date(myTicketDetail.createdAt).toLocaleString() : ''}
                      {myTicketDetail.updatedAt && myTicketDetail.updatedAt !== myTicketDetail.createdAt && (
                        <> · Last updated {new Date(myTicketDetail.updatedAt).toLocaleString()}</>
                      )}
                    </Typography>
                  </Paper>
                  <Typography variant="subtitle2" gutterBottom>Replies</Typography>
                  {(myTicketDetail.replies || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">No replies yet.</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {myTicketDetail.replies.map((r) => (
                        <Paper key={r.id} variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
                          <Typography variant="caption" color="text.secondary">{r.createdBy} · {r.createdAt ? new Date(r.createdAt).toLocaleString() : ''}</Typography>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>{r.message}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">Ticket not found.</Typography>
              )}
            </>
          ) : (
            <>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 600, color: '#000' }} id="my-tickets-title">
                  My tickets
                </Typography>
                <Button size="small" startIcon={<AddIcon />} onClick={() => { setMyTicketsDrawerOpen(false); setCreateTicketDrawerOpen(true); }} sx={{ textTransform: 'none' }}>
                  Create ticket
                </Button>
              </Stack>
              {myTicketsLoading ? (
                <Skeleton variant="rectangular" height={120} sx={{ mb: 1 }} />
              ) : (myTicketsData?.data || []).length === 0 ? (
                <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
                  <Typography variant="body2" color="text.secondary">No tickets yet.</Typography>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setMyTicketsDrawerOpen(false); setCreateTicketDrawerOpen(true); }} sx={{ textTransform: 'none' }}>
                    Create ticket
                  </Button>
                </Stack>
              ) : (
                <>
                  <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
                    <Stack spacing={1.5}>
                      {(myTicketsData?.data || []).map((t) => {
                        const statusConfig = getStatusConfig(t.status);
                        return (
                          <Card
                            key={t.id}
                            variant="outlined"
                            sx={{
                              cursor: 'pointer',
                              transition: 'box-shadow 0.2s, border-color 0.2s',
                              '&:hover': {
                                boxShadow: 1,
                                borderColor: 'primary.main',
                              },
                            }}
                            onClick={() => setSelectedMyTicketId(t.id)}
                          >
                            <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                              <Typography variant="body2" sx={{ mb: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                                {t.descriptionSnippet}
                              </Typography>
                              <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={0.5}>
                                <Typography variant="caption" color="text.secondary">
                                  {t.problemType || '–'} · {t.createdAt ? new Date(t.createdAt).toLocaleDateString() : ''}
                                </Typography>
                                <Chip
                                  label={statusConfig.label}
                                  size="small"
                                  sx={{
                                    backgroundColor: statusConfig.color,
                                    color: statusConfig.text,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              </Stack>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </Stack>
                  </Box>
                  {((myTicketsData?.totalPages ?? 1) > 1) && (
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary">
                        Page {myTicketsPage} of {myTicketsData?.totalPages ?? 1}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          disabled={myTicketsPage <= 1}
                          onClick={() => setMyTicketsPage((p) => Math.max(1, p - 1))}
                          sx={{ textTransform: 'none' }}
                        >
                          Previous
                        </Button>
                        <Button
                          size="small"
                          disabled={myTicketsPage >= (myTicketsData?.totalPages ?? 1)}
                          onClick={() => setMyTicketsPage((p) => p + 1)}
                          sx={{ textTransform: 'none' }}
                        >
                          Next
                        </Button>
                      </Stack>
                    </Stack>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

export default HelpSupport;
